import { useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, Sparkles, Text, Edges } from "@react-three/drei";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, ExternalLink, Rocket, Eye, Sparkles as Spark, Clapperboard, Share2, Minimize2, Maximize2, BellOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/* RUNNER ALERT — immersive 3D takeover when the grokbot + agents confirm a potential runner. Acid green on obsidian, Higgsfield cut behind the coin. */

const ACID = "#CCFF00";
const fmtPx = (p) => (p == null ? "—" : Number(p) >= 1 ? Number(p).toFixed(4) : Number(p).toPrecision(3));
const API_ORIGIN = process.env.REACT_APP_MEME_API_URL || process.env.REACT_APP_BACKEND_URL;
const compact = (n) => (n == null ? "—" : Number(n) >= 1e6 ? `$${(n / 1e6).toFixed(2)}M` : Number(n) >= 1e3 ? `$${(n / 1e3).toFixed(1)}K` : `$${Number(n).toFixed(0)}`);
export const CLASS_TONE = { NEW: ACID, WATCHING: "#F5B942", GAINING: "#00E5B5", ACCELERATING: ACID, HOLDING: "#7F8BA3", LOSING: "#FF3355", INVALIDATED: "#FF3355", COMPLETED: "#38BDF8" };
export const readPrefs = () => { try { return { mode: "immersive", motion: true, ...JSON.parse(localStorage.getItem("ripp-runner-prefs") || "{}") }; } catch { return { mode: "immersive", motion: true }; } };
export const writePrefs = (p) => { localStorage.setItem("ripp-runner-prefs", JSON.stringify(p)); window.dispatchEvent(new CustomEvent("ripp-runner-prefs")); };
const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

const Coin = ({ score }) => {
  const g = useRef();
  const inner = useRef();
  useFrame((st, dt) => {
    if (g.current) { g.current.rotation.y += dt * 0.55; g.current.rotation.x = Math.sin(st.clock.elapsedTime * 0.6) * 0.18; }
    if (inner.current) inner.current.rotation.y -= dt * 0.9;
  });
  const glow = 0.25 + Math.min(1, (score || 60) / 100) * 0.55;
  return (
    <group ref={g}>
      <mesh>
        <icosahedronGeometry args={[1.35, 1]} />
        <meshPhysicalMaterial color="#070907" metalness={0.55} roughness={0.42} emissive={ACID} emissiveIntensity={glow * 0.035} clearcoat={1} clearcoatRoughness={0.15} flatShading />
        <Edges threshold={8} color={ACID} />
      </mesh>
      <mesh ref={inner}>
        <icosahedronGeometry args={[0.72, 0]} />
        <meshStandardMaterial color={ACID} emissive={ACID} emissiveIntensity={glow * 1.6} wireframe />
      </mesh>
    </group>
  );
};

const Ring = ({ r, tilt, speed, color, label }) => {
  const ref = useRef();
  useFrame((_, dt) => { if (ref.current) ref.current.rotation.z += dt * speed; });
  return (
    <group rotation={[tilt, 0, 0]}>
      <group ref={ref}>
        <mesh>
          <torusGeometry args={[r, 0.012, 8, 160]} />
          <meshStandardMaterial color={color} emissive={color} emissiveIntensity={1.2} transparent opacity={0.75} />
        </mesh>
        {label && <Text position={[r, 0, 0]} rotation={[-tilt, 0, 0]} fontSize={0.19} color={color} anchorX="center" anchorY="middle" outlineWidth={0.004} outlineColor="#000">{label}</Text>}
      </group>
    </group>
  );
};

const Slam = ({ children }) => {
  const g = useRef();
  const t0 = useRef(null);
  useFrame((st) => {
    if (!g.current) return;
    if (t0.current == null) t0.current = st.clock.elapsedTime;
    const t = Math.min(1, (st.clock.elapsedTime - t0.current) / 0.9);
    const k = 1 - Math.pow(1 - t, 3);
    const s = 1.9 - 0.9 * k + Math.sin(t * Math.PI) * 0.08;
    g.current.scale.setScalar(s);
    g.current.position.z = (1 - k) * 2.5;
  });
  return <group ref={g}>{children}</group>;
};

const Scene = ({ a, hideCoin }) => {
  const live = a.live || {};
  const price = live.px ?? a.entry?.px;
  const xLabel = live.x != null ? `${Number(live.x).toFixed(2)}x` : a.kind === "BUY" ? "1.00x" : "WATCH";
  return (
    <>
      <ambientLight intensity={0.35} />
      <pointLight position={[4, 3, 4]} intensity={28} color={ACID} />
      <pointLight position={[-4, -2, 2]} intensity={16} color="#38BDF8" />
      <pointLight position={[0, -4, -3]} intensity={18} color="#FF3355" />
      <Slam>
        {!hideCoin && <Float speed={1.6} rotationIntensity={0.25} floatIntensity={0.9}>
          <Coin score={a.grok?.score} />
        </Float>}
        <Ring r={2.15} tilt={1.25} speed={0.5} color={ACID} label={price != null ? `$${fmtPx(price)}` : null} />
        <Ring r={2.65} tilt={0.55} speed={-0.32} color="#38BDF8" label={xLabel} />
        <Ring r={3.1} tilt={1.9} speed={0.21} color="#A78BFA" label={a.grok?.score != null ? `GROK ${a.grok.score}` : null} />
        <Text position={[0, -2.55, 0]} fontSize={0.58} color="#ffffff" anchorX="center" anchorY="middle" letterSpacing={0.08} outlineWidth={0.008} outlineColor={ACID}>
          {`$${a.symbol}`}
        </Text>
      </Slam>
      <Sparkles count={220} scale={[9, 6, 6]} size={2.2} speed={0.6} color={ACID} opacity={0.7} />
      <Sparkles count={60} scale={[7, 5, 5]} size={4} speed={0.25} color="#ffffff" opacity={0.35} />
    </>
  );
};

const Dial = ({ v }) => {
  const r = 26, c = 2 * Math.PI * r, p = Math.max(0, Math.min(100, Number(v) || 0)) / 100;
  return (
    <svg width="68" height="68" viewBox="0 0 68 68" data-testid="runner-alert-grok-dial">
      <circle cx="34" cy="34" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="5" fill="none" />
      <circle cx="34" cy="34" r={r} stroke={ACID} strokeWidth="5" fill="none" strokeDasharray={c} strokeDashoffset={c * (1 - p)} strokeLinecap="round" transform="rotate(-90 34 34)" style={{ filter: `drop-shadow(0 0 6px ${ACID})`, transition: "stroke-dashoffset 1.2s cubic-bezier(.2,.8,.2,1)" }} />
      <text x="34" y="38" textAnchor="middle" fill="#fff" fontSize="16" fontFamily="JetBrains Mono, monospace" fontWeight="700">{v ?? "—"}</text>
    </svg>
  );
};

const Chip = ({ tone = ACID, children, testid }) => (
  <span data-testid={testid} className="inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em]"
    style={{ color: tone, borderColor: `${tone}66`, background: `${tone}14` }}>{children}</span>
);

const NftCard = ({ ref: _r, nft }) => nft ? (
  <motion.div initial={{ opacity: 0, x: -40, rotateY: 25 }} animate={{ opacity: 1, x: 0, rotateY: 0 }} transition={{ delay: 0.9, duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
    className="absolute left-8 bottom-8 w-[150px] rounded-md overflow-hidden border" style={{ borderColor: `${ACID}55`, boxShadow: `0 0 40px ${ACID}33, inset 0 0 0 1px rgba(255,255,255,0.05)`, background: "rgba(0,0,0,0.55)" }} data-testid="runner-alert-nft">
    <img src={`${API_ORIGIN}${nft.poster_url}`} alt={nft.name} className="w-full aspect-square object-cover" />
    <div className="px-2 py-1.5 text-[9px] font-mono leading-tight">
      <div className="text-white font-bold">{nft.character_name} <span className="text-[var(--r2-text2)]">· {nft.name}</span></div>
      <div className="text-[var(--r2-text2)] tracking-[0.14em] uppercase">Genesis · {nft.tier} · presenting</div>
    </div>
  </motion.div>
) : null;

const Stat = ({ k, v, testid, tone }) => (
  <div><div className="text-[9px] font-mono tracking-[0.2em] text-[var(--r2-text2)]">{k}</div><div className="text-sm font-mono font-bold" style={{ color: tone || "#fff" }} data-testid={testid}>{v}</div></div>
);

export const RunnerAlertTakeover = ({ alert: a, onClose, onCompact }) => {
  const [tick, setTick] = useState(0);
  const [phase, setPhase] = useState("discovery");
  const [sharing, setSharing] = useState(false);
  useEffect(() => { const t = setTimeout(() => setPhase("reveal"), 2200); return () => clearTimeout(t); }, []);
  const share = async () => { setSharing(true); try { const r = await api.meme.runnerAlertPost(a.id); toast[r.status === "POSTED" ? "success" : "error"](r.status === "POSTED" ? "Shared on X" : r.error || "X post failed"); } catch (e) { toast.error(e?.response?.data?.detail || "Could not share"); } finally { setSharing(false); } };
  useEffect(() => { const id = setInterval(() => setTick((t) => t + 1), 1000); const auto = setTimeout(onClose, 45000); return () => { clearInterval(id); clearTimeout(auto); }; }, [onClose]);
  useEffect(() => { const k = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  const video = a.video || {}; const x = a.x || {}; const live = a.live || {}; const g = a.grok || {}; const ag = a.agent || {};
  const isBuy = a.kind === "BUY";
  const hasCut = video.status === "READY" && !!video.url;
  const [cutPlaying, setCutPlaying] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(a.mint || ""); toast.success("CA copied"); };
  const stagger = (i) => ({ initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.35 + i * 0.09, duration: 0.5, ease: [0.2, 0.8, 0.2, 1] } });
  const cls = a.runner_classification || (isBuy ? "NEW" : "WATCHING");
  const ms = a.market_snapshot || {};
  if (phase === "discovery") return (
    <motion.div className="fixed inset-0 z-[80] pointer-events-none" data-testid="runner-alert-discovery" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.div className="absolute inset-0" initial={{ boxShadow: `inset 0 0 0px ${ACID}00` }} animate={{ boxShadow: [`inset 0 0 0px ${ACID}00`, `inset 0 0 160px ${ACID}55`, `inset 0 0 60px ${ACID}22`] }} transition={{ duration: 2.2, times: [0, 0.5, 1] }} />
      <motion.div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-3" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }}>
        <motion.div className="rounded-full border" style={{ width: 120, height: 120, borderColor: ACID, boxShadow: `0 0 60px ${ACID}88` }} animate={{ scale: [1, 1.6, 2.4], opacity: [0.9, 0.5, 0] }} transition={{ duration: 1.8, repeat: Infinity }} />
        <div className="font-mono text-[11px] tracking-[0.4em] uppercase" style={{ color: ACID, textShadow: `0 0 18px ${ACID}` }}>HIVE · runner detected · ${a.symbol}</div>
      </motion.div>
    </motion.div>
  );
  return (
    <motion.div className="fixed inset-0 z-[80] flex" data-testid="runner-alert-takeover" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.35 } }}
      style={{ background: "radial-gradient(1200px 700px at 35% 50%, rgba(204,255,0,0.10), transparent 60%), rgba(4,6,4,0.92)", backdropFilter: "blur(18px)" }}>
      {hasCut && (
        <motion.video key={video.url} src={video.url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${video.url}` : video.url} autoPlay loop muted playsInline preload="auto" data-testid="runner-alert-video" className="absolute inset-y-0 left-0 w-[58%] h-full object-cover"
          onPlaying={() => setCutPlaying(true)} onError={() => setCutPlaying(false)}
          initial={{ opacity: 0 }} animate={{ opacity: cutPlaying ? 0.95 : 0 }} transition={{ duration: 1.2 }}
          style={{ WebkitMaskImage: "linear-gradient(90deg, #000 70%, transparent 100%)", maskImage: "linear-gradient(90deg, #000 70%, transparent 100%)" }} />
      )}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='120' height='120' filter='url(%23n)' opacity='0.06'/></svg>\")" }} />
      <motion.div className="absolute inset-y-0 left-0 w-[58%]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
        <Canvas camera={{ position: [0, 0.3, 9.5], fov: 40 }} gl={{ antialias: true, alpha: true }} dpr={[1, 1.75]} data-testid="runner-alert-canvas">
          <Scene a={a} hideCoin={cutPlaying} />
        </Canvas>
        <NftCard nft={a.nft_ref} />
      </motion.div>
      <div className="ml-auto w-[42%] h-full flex flex-col justify-center pr-12 pl-6 gap-5 relative" data-testid="runner-alert-panel">
        <div className="absolute top-6 right-8 flex items-center gap-4 text-[10px] font-mono tracking-[0.2em] text-[var(--r2-text2)]">
          <button onClick={onCompact} data-testid="runner-alert-compact" className="flex items-center gap-1 hover:text-white"><Minimize2 size={11} /> COMPACT</button>
          <button onClick={onClose} data-testid="runner-alert-close" className="flex items-center gap-1 hover:text-white"><X size={12} /> DISMISS · ESC</button>
        </div>
        <motion.div {...stagger(0)} className="flex items-center gap-2">
          <Chip tone={isBuy ? ACID : "#F5B942"} testid="runner-alert-kind">{isBuy ? <><Rocket size={10} /> LIVE ENTRY</> : <><Eye size={10} /> RUNNER WATCH</>}</Chip>
          <Chip tone={CLASS_TONE[cls] || "#7F8BA3"} testid="runner-alert-class">{cls}</Chip>
          <Chip tone="#38BDF8" testid="runner-alert-lane">{ag.lane || ag.strategy || g.stage || "LAUNCH"}</Chip>
          <span className="text-[10px] font-mono text-[var(--r2-text2)]" data-testid="runner-alert-detected">{a.chain || "solana"} · detected {new Date((a.detected_at || a.t) * 1000).toISOString().slice(11, 19)} UTC · {Math.max(0, Math.round(Date.now() / 1000 - a.t))}s ago{a.freshness_sec != null && a.live ? ` · data ${a.freshness_sec}s old` : ""}</span>
        </motion.div>
        <motion.div {...stagger(1)}>
          <div className="font-display text-5xl sm:text-6xl font-black tracking-tight text-white leading-none" style={{ textShadow: `0 0 28px ${ACID}80` }} data-testid="runner-alert-symbol">${a.symbol}</div>
          {a.entry?.name && a.entry.name !== a.symbol && <div className="mt-1 text-sm text-[var(--r2-text2)]">{a.entry.name}</div>}
          {a.mint && <div className="mt-2 flex flex-wrap items-center gap-3 pointer-events-auto">
            <button onClick={copy} data-testid="runner-alert-ca" className="flex items-center gap-1.5 text-[10px] font-mono text-[var(--r2-text2)] hover:text-white"><Copy size={10} /> {a.mint}</button>
            <a href={`https://dexscreener.com/solana/${a.mint}`} target="_blank" rel="noreferrer" data-testid="runner-alert-dexscreener" className="flex items-center gap-1 text-[10px] font-mono font-bold tracking-[0.16em]" style={{ color: ACID }}><ExternalLink size={10} /> DEXSCREENER</a>
          </div>}
        </motion.div>
        <motion.div {...stagger(2)} className="flex items-center gap-5">
          <Dial v={g.score} />
          <div className="flex flex-col gap-1.5">
            <div className="text-[9px] font-mono tracking-[0.2em] text-[var(--r2-text2)]">GROKBOT · X CROWD</div>
            <div className="flex items-center gap-2">
              <Chip tone={g.organic === false ? "#FF3355" : ACID} testid="runner-alert-grok-verdict">{g.verdict || (g.organic === false ? "BOTS" : "ORGANIC")}{g.confidence != null ? ` ${Math.round(g.confidence * 100)}%` : ""}</Chip>
              {g.velocity && <Chip tone="#A78BFA">{g.velocity}</Chip>}
              {g.distinct_accounts != null && <Chip tone="#7F8BA3">{g.distinct_accounts} accounts</Chip>}
            </div>
            {g.why && <div className="text-sm text-white/85 max-w-md leading-snug" data-testid="runner-alert-grok-why">{g.why}</div>}
            {(g.handles || g.kols)?.length ? <div className="text-[10px] font-mono text-[var(--r2-text2)]">{(g.handles || g.kols).slice(0, 5).map((h) => `@${h}`).join(" · ")}</div> : null}
          </div>
        </motion.div>
        <motion.div {...stagger(3)} className="grid grid-cols-3 gap-3 border-t border-white/10 pt-4">
          <div><div className="text-[9px] font-mono tracking-[0.2em] text-[var(--r2-text2)]">AGENTS</div><div className="text-base font-mono font-bold text-[var(--r2-emerald)]" data-testid="runner-alert-agent">{ag.decision || "CONFIRMED"}{ag.buy_votes ? <span className="text-[var(--r2-text2)] text-xs"> · {ag.buy_votes} votes</span> : null}</div></div>
          <div><div className="text-[9px] font-mono tracking-[0.2em] text-[var(--r2-text2)]">{isBuy ? "ENTRY" : "PRICE"}</div><div className="text-base font-mono font-bold text-white" data-testid="runner-alert-entry">{a.entry?.px != null ? `$${fmtPx(a.entry.px)}` : "—"}{a.entry?.sol ? <span className="text-[var(--r2-text2)] text-xs"> · {a.entry.sol} SOL</span> : null}</div></div>
          <div><div className="text-[9px] font-mono tracking-[0.2em] text-[var(--r2-text2)]">LIVE</div><div className="text-base font-mono font-bold" style={{ color: live.x == null ? "#7F8BA3" : live.x >= 1 ? ACID : "#FF3355" }} data-testid="runner-alert-live">{live.x != null ? `${Number(live.x).toFixed(2)}x` : isBuy ? "filling…" : "watching"}{live.peak_x ? <span className="text-[var(--r2-text2)] text-xs"> · peak {Number(live.peak_x).toFixed(2)}x</span> : null}</div></div>
        </motion.div>
        {Object.keys(ms).length > 0 && <motion.div {...stagger(3.5)} className="grid grid-cols-5 gap-3 border-t border-white/10 pt-3" data-testid="runner-alert-market">
          <Stat k="LIQUIDITY" v={compact(ms.liquidity_usd)} /><Stat k="HOLDERS" v={ms.holders ?? "—"} /><Stat k="MCAP" v={compact(ms.mcap)} />
          <Stat k="5M MOVE" v={ms.pc5m != null ? `${ms.pc5m >= 0 ? "+" : ""}${Number(ms.pc5m).toFixed(1)}%` : "—"} tone={ms.pc5m >= 0 ? "#00E5B5" : "#FF3355"} /><Stat k="AGE" v={ms.age_min != null ? `${Math.round(ms.age_min)}m` : "—"} />
        </motion.div>}
        <motion.div {...stagger(4)} className="flex items-center gap-3 text-[10px] font-mono">
          <button onClick={share} disabled={sharing || x.status === "POSTED"} data-testid="runner-alert-share" className="flex items-center gap-1.5 rounded-sm px-3 py-1.5 font-bold tracking-[0.16em] text-black disabled:opacity-50" style={{ background: ACID }}><Share2 size={11} /> {x.status === "POSTED" ? "SHARED ON X" : sharing ? "SHARING…" : "SHARE ON X"}</button>
          <Chip tone={video.status === "READY" ? ACID : video.status === "RENDERING" ? "#F5B942" : video.status === "FAILED" ? "#FF3355" : "#7F8BA3"} testid="runner-alert-video-status">
            <Clapperboard size={10} /> HIGGSFIELD {video.status === "RENDERING" ? `RENDERING ${tick}s` : video.status || "OFF"}
          </Chip>
          <Chip tone={x.status === "POSTED" ? "#38BDF8" : x.status === "PENDING" ? "#F5B942" : x.status === "FAILED" ? "#FF3355" : "#7F8BA3"} testid="runner-alert-x-status">
            <Spark size={10} /> X {x.status || "OFF"}{x.tweet_id ? " ↗" : ""}
          </Chip>
          {video.error && <span className="text-[var(--r2-red)] truncate max-w-xs" title={video.error}>{video.error}</span>}
        </motion.div>
      </div>
    </motion.div>
  );
};

/* Polls the alert feed, queues unseen alerts and shows them one at a time; `replay` re-opens any alert. */
export const useRunnerAlerts = (enabled = true) => {
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(null);
  const seen = useRef(new Set(JSON.parse(sessionStorage.getItem("ripp-runner-seen") || "[]")));
  const currentRef = useRef(null);
  useEffect(() => { currentRef.current = current; }, [current]);
  const bootAt = useRef(Date.now() / 1000);
  useEffect(() => {
    if (!enabled) return undefined;
    let stop = false;
    const poll = async () => {
      try {
        const r = await api.meme.runnerAlerts();
        if (stop) return;
        const rows = r.items || [];
        setItems(rows);
        if (currentRef.current) {
          const fresh = rows.find((x) => x.id === currentRef.current.id);
          if (fresh) setCurrent((cur) => (cur ? { ...cur, ...fresh } : cur));
          return;
        }
        rows.filter((x) => x.t <= bootAt.current - 120).forEach((x) => seen.current.add(x.id));   // history belongs to the UNIVERSE gallery, not the live takeover
        const next = rows.find((x) => !seen.current.has(x.id));
        if (next) { seen.current.add(next.id); sessionStorage.setItem("ripp-runner-seen", JSON.stringify([...seen.current].slice(-50))); setCurrent(next); }
      } catch { /* feed is best-effort */ }
    };
    poll(); const id = setInterval(poll, 5000);
    return () => { stop = true; clearInterval(id); };
  }, [enabled]);
  const replay = (a) => setCurrent(a);
  const close = useMemo(() => () => setCurrent(null), []);
  return { items, current, replay, close };
};

export const RunnerAlertCompact = ({ alert: a, onClose, onExpand }) => {
  const g = a.grok || {}; const cls = a.runner_classification || (a.kind === "BUY" ? "NEW" : "WATCHING"); const live = a.live || {};
  return (
    <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} className="fixed bottom-4 right-4 z-[80] w-[360px] rounded-md border overflow-hidden" data-testid="runner-alert-compact-card"
      style={{ borderColor: `${ACID}66`, background: "rgba(6,8,6,0.92)", backdropFilter: "blur(14px)", boxShadow: `0 0 36px ${ACID}33` }}>
      <div className="flex gap-3 p-3">
        {a.nft_ref && <img src={`${API_ORIGIN}${a.nft_ref.poster_url}`} alt="" className="w-16 h-16 rounded object-cover shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2"><Chip tone={a.kind === "BUY" ? ACID : "#F5B942"}>{a.kind === "BUY" ? "LIVE ENTRY" : "RUNNER WATCH"}</Chip><Chip tone={CLASS_TONE[cls]}>{cls}</Chip></div>
          <div className="mt-1 text-xl font-black text-white leading-none">${a.symbol} <span className="text-xs font-mono text-[var(--r2-text2)]">grok {g.score} · {g.verdict || (g.organic === false ? "BOTS" : "ORGANIC")}</span>
            {a.mint && <a href={`https://dexscreener.com/solana/${a.mint}`} target="_blank" rel="noreferrer" data-testid="runner-alert-compact-dexscreener" className="ml-2 inline-flex items-center gap-1 align-middle text-[10px] font-mono font-bold tracking-[0.16em]" style={{ color: ACID }}><ExternalLink size={10} /> DEX</a>}</div>
          <div className="text-[10px] font-mono text-[var(--r2-text2)] truncate">{g.why}</div>
          <div className="mt-1 text-[10px] font-mono" style={{ color: live.x == null ? "#7F8BA3" : live.x >= 1 ? ACID : "#FF3355" }}>{live.x != null ? `${Number(live.x).toFixed(2)}x live` : a.kind === "BUY" ? "filling…" : "watching"}</div>
        </div>
      </div>
      <div className="flex items-center gap-3 px-3 py-1.5 border-t border-white/10 text-[10px] font-mono tracking-[0.16em]">
        <button onClick={onExpand} data-testid="runner-alert-expand" className="flex items-center gap-1 hover:text-white" style={{ color: ACID }}><Maximize2 size={10} /> IMMERSIVE</button>
        <button onClick={onClose} data-testid="runner-alert-compact-close" className="ml-auto text-[var(--r2-text2)] hover:text-white">DISMISS</button>
      </div>
    </motion.div>
  );
};

export const RunnerAlertHost = ({ current, onClose }) => {
  const [prefs, setPrefs] = useState(readPrefs);
  const [forceFull, setForceFull] = useState(false);
  useEffect(() => { setForceFull(false); }, [current?.id]);
  useEffect(() => { const h = () => setPrefs(readPrefs()); window.addEventListener("ripp-runner-prefs", h); return () => window.removeEventListener("ripp-runner-prefs", h); }, []);
  if (!current || prefs.mode === "off") return null;
  const immersive = forceFull || (prefs.mode === "immersive" && !reducedMotion());
  const toCompact = () => { const p = { ...prefs, mode: "compact" }; setPrefs(p); writePrefs(p); setForceFull(false); toast.info("Runner alerts now compact — change under LIMITS → RUNNER EVENTS"); };
  return (
    <AnimatePresence>
      {immersive ? <RunnerAlertTakeover key={current.id} alert={current} onClose={onClose} onCompact={toCompact} />
        : <RunnerAlertCompact key={`c-${current.id}`} alert={current} onClose={onClose} onExpand={() => setForceFull(true)} />}
    </AnimatePresence>
  );
};

export const RunnerPrefs = () => {
  const [p, setP] = useState(readPrefs);
  const set = (mode) => { const n = { ...p, mode }; setP(n); writePrefs(n); };
  return (
    <div className="flex items-center gap-2 text-[9px] font-mono" data-testid="runner-prefs">
      <BellOff size={10} className="text-[var(--r2-text2)]" />
      {["immersive", "compact", "off"].map((m) => <button key={m} onClick={() => set(m)} data-testid={`runner-pref-${m}`} className={`uppercase tracking-[0.16em] ${p.mode === m ? "text-[var(--meme)]" : "text-[var(--r2-text2)] hover:text-[var(--r2-text1)]"}`}>{m}</button>)}
    </div>
  );
};
